import { useState, useEffect, useCallback } from 'react'
import { ArrowUpDown, Save, GripVertical, AlertCircle, CheckCircle, RotateCcw } from 'lucide-react'
import { apiService } from '../services/api'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import ErrorMessage from '../components/ui/ErrorMessage'

// Operator display configuration
const OPERATOR_CONFIG = {
  'ORANGE': { name: 'Orange', color: 'bg-orange-500', textColor: 'text-orange-600' },
  'TMOBILE': { name: 'T-Mobile', color: 'bg-pink-500', textColor: 'text-pink-600' },
  'PLUS': { name: 'Plus', color: 'bg-green-500', textColor: 'text-green-600' },
  'P4': { name: 'Play', color: 'bg-purple-500', textColor: 'text-purple-600' }
}

export default function AdminOperatorOrderPage() {
  const [operators, setOperators] = useState([])
  const [originalOrder, setOriginalOrder] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)

  // Load operator order
  useEffect(() => {
    loadOperatorOrder()
  }, [])

  const loadOperatorOrder = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.getOperatorOrder()
      const ops = response.data.operators || []
      setOperators(ops)
      setOriginalOrder(ops.map(o => o.operator_code))
    } catch (err) {
      console.error('Error loading operator order:', err)
      setError('Nie udało się załadować kolejności operatorów')
    } finally {
      setLoading(false)
    }
  }

  // Check if order has changed
  const hasChanges = useCallback(() => {
    const currentOrder = operators.map(o => o.operator_code)
    return JSON.stringify(currentOrder) !== JSON.stringify(originalOrder)
  }, [operators, originalOrder])

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', index)
    // Add visual feedback
    e.currentTarget.classList.add('opacity-50')
  }

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('opacity-50')
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (index !== draggedIndex) {
      setDragOverIndex(index)
    }
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    const dragIndex = draggedIndex
    
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragOverIndex(null)
      return
    }

    const newOperators = [...operators]
    const [draggedItem] = newOperators.splice(dragIndex, 1)
    newOperators.splice(dropIndex, 0, draggedItem)
    
    setOperators(newOperators)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  // Move operators up/down with buttons (accessibility)
  const moveOperator = (index, direction) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= operators.length) return

    const newOperators = [...operators]
    const [item] = newOperators.splice(index, 1)
    newOperators.splice(newIndex, 0, item)
    setOperators(newOperators)
  }

  // Save order
  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)
      
      const operatorCodes = operators.map(o => o.operator_code)
      await apiService.updateOperatorOrder(operatorCodes)
      
      setOriginalOrder(operatorCodes)
      setSuccess('Kolejność operatorów została zapisana')
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error saving operator order:', err)
      setError('Nie udało się zapisać kolejności operatorów: ' + (err.response?.data?.detail || err.message))
    } finally {
      setSaving(false)
    }
  }

  // Reset to original order
  const handleReset = () => {
    const resetOperators = originalOrder.map((code, index) => ({
      operator_code: code,
      display_order: index,
      operator_name: OPERATOR_CONFIG[code]?.name || code
    }))
    setOperators(resetOperators)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <ArrowUpDown className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Kolejność operatorów</h1>
        </div>
        <p className="text-gray-600">
          Przeciągnij i upuść operatorów, aby zmienić ich kolejność wyświetlania na dashboardzie.
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center space-x-2">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}
      
      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} />
        </div>
      )}

      {/* Operator List */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h2 className="text-sm font-medium text-gray-700">Kolejność wyświetlania</h2>
        </div>
        
        <ul className="divide-y divide-gray-200">
          {operators.map((operator, index) => {
            const config = OPERATOR_CONFIG[operator.operator_code] || {}
            const isDragging = draggedIndex === index
            const isDragOver = dragOverIndex === index
            
            return (
              <li
                key={operator.operator_code}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                className={`
                  flex items-center px-4 py-4 cursor-grab active:cursor-grabbing transition-all
                  ${isDragging ? 'opacity-50 bg-gray-100' : 'bg-white hover:bg-gray-50'}
                  ${isDragOver ? 'border-t-2 border-blue-500' : ''}
                `}
              >
                {/* Drag Handle */}
                <div className="flex-shrink-0 mr-4 text-gray-400">
                  <GripVertical className="h-5 w-5" />
                </div>
                
                {/* Position Number */}
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-4">
                  <span className="text-sm font-semibold text-gray-600">{index + 1}</span>
                </div>
                
                {/* Operator Color Indicator */}
                <div className={`flex-shrink-0 w-3 h-3 rounded-full mr-3 ${config.color || 'bg-gray-400'}`} />
                
                {/* Operator Name */}
                <div className="flex-1">
                  <span className={`font-medium ${config.textColor || 'text-gray-900'}`}>
                    {config.name || operator.operator_name || operator.operator_code}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    ({operator.operator_code})
                  </span>
                </div>
                
                {/* Move Buttons (for accessibility) */}
                <div className="flex-shrink-0 flex items-center space-x-1">
                  <button
                    onClick={() => moveOperator(index, -1)}
                    disabled={index === 0}
                    className={`p-1.5 rounded transition-colors ${
                      index === 0 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                    title="Przesuń w górę"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => moveOperator(index, 1)}
                    disabled={index === operators.length - 1}
                    className={`p-1.5 rounded transition-colors ${
                      index === operators.length - 1 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                    title="Przesuń w dół"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={handleReset}
          disabled={!hasChanges() || saving}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
            hasChanges() && !saving
              ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
              : 'border-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <RotateCcw className="h-4 w-4" />
          <span>Przywróć</span>
        </button>
        
        <button
          onClick={handleSave}
          disabled={!hasChanges() || saving}
          className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-colors ${
            hasChanges() && !saving
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? (
            <LoadingSpinner size="sm" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>{saving ? 'Zapisywanie...' : 'Zapisz zmiany'}</span>
        </button>
      </div>

      {/* Info Note */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Informacja</p>
            <p>
              Zmiana kolejności operatorów wpłynie na ich wyświetlanie w sekcji "Przegląd Operatorów" 
              na głównym dashboardzie. Zmiany będą widoczne dla wszystkich użytkowników po odświeżeniu strony.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
